import { supabase } from '../services/supabaseClient'

const initApi = (token) => {
  return {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  }
}

export const fetchUserRole = async (userId) => {
  const { data, error } = await supabase
    .from('users')
    .select('role')
    .eq('id', userId)
    .single()

  if (!error && data) {
    return data.role
  } else {
    console.error('Error fetching user role:', error)
  }
}

// Get Profile API
export const getProfile = async () => {
  const { data, error } = await supabase.auth.getUser()

  if (error) {
    throw error // Handle any error that occurs
  }

  return data.user
}

export const getUsers = async (page, perPage, search) => {
  let query = supabase
    .from('users')
    .select('*', { count: 'exact' })
    .range((page - 1) * perPage, page * perPage - 1)

  if (search) {
    query = query.ilike('full_name', `%${search}%`)
  }

  const { data, count, error } = await query

  if (error) {
    console.error('Error fetching users:', error.message)
    return []
  }

  return { users: data, total: count }
}

export const getUserCourseStatus = async (page, perPage, search, status) => {
  let query = supabase
    .from('user_course_status_2')
    .select('*', { count: 'exact' })
    .range((page - 1) * perPage, page * perPage - 1)

  if (search) {
    query = query.ilike('full_name', `%${search}%`)
  }

  if (status !== 'all') {
    query = query.eq('overall_status', status)
  }

  query = query.order('created_at', { ascending: false })

  const { data, count, error } = await query

  if (error) {
    console.error('Error fetching users:', error.message)
    return []
  }

  return { users: data, total: count }
}

export const getUser = async (id) => {
  const { data, error } = await supabase
    .from('users')
    .select(
      `
            *,
            avatars (*)
        `
    )
    .eq('id', id)
    .single()

  if (error) {
    console.error('Error fetching users:', error.message)
    return []
  }

  return data
}

export const upsertAvatar = async (
  userId,
  avatarUrl,
  readyplayerUserId,
  readyplayerAvatarId
) => {
  const { data, error } = await supabase.from('avatars').upsert(
    [
      {
        user_id: userId,
        readyplayer_url: avatarUrl,
        readyplayer_user_id: readyplayerUserId,
        readyplayer_avatar_id: readyplayerAvatarId,
      },
    ],
    { onConflict: 'user_id' }
  )

  if (error) {
    console.error('Error creating avatar:', error.message)
    return []
  }

  return data
}

export const getUserAvatar = async (id) => {
  const { data, error } = await supabase
    .from('avatars')
    .select('*')
    .eq('user_id', id)
    .single()

  if (error) {
    console.error('Error fetching users:', error.message)
    return []
  }

  return data
}

export const getMyCourses = async (userId, type) => {
  if (!userId) return []
  let query = supabase
    .from('courses')
    .select(
      `
          *,
          badges (
                  *
              ),
          course_badges(
              user_badge_progress (
                  *
              )
            ),
          stages (
              *,
              badges (
                  *
              ),
              stage_badges(
                user_badge_progress (
                    *
                )
              )
          )
        `
    )
    .eq('stages.stage_badges.user_badge_progress.user_id', userId)
    .eq('course_badges.user_badge_progress.user_id', userId)
    .order('order')

  if (type) {
    query = query.eq('type', type)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching course:', error.message)
    return []
  }

  return data
}

export const getCourse = async (id, userId) => {
  const { data, error } = await supabase
    .from('courses')
    .select(
      `
            *,
            badges (
                    *
                ),
            course_badges(
                user_badge_progress (
                    *
                )
              ),
            stages (
                *,
                badges (
                    *
                ),
                stage_badges(
                  id,
                  user_badge_progress (
                      *
                  )
                )
            )
        `
    )
    .eq('id', id)
    .eq('stages.stage_badges.user_badge_progress.user_id', userId)
    .eq('course_badges.user_badge_progress.user_id', userId)
    .order('order', { foreignTable: 'stages' })
    .single()

  if (error) {
    console.error('Error fetching course:', error.message)
    return null
  }

  return data
}

export const startCourse = async (userId, sourceId, token) => {
  try {
    const apiOptions = initApi(token)

    const body = JSON.stringify({
      userId,
      sourceId,
    })

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/start-course`,
      {
        ...apiOptions,
        method: 'POST',
        body,
      }
    )

    const data = await response.json()

    if (!response.ok) {
      console.error(data.error)
      return
    }

    return data
  } catch (e) {
    console.error(e)
  }
}

export const updateBadgeCompletion = async (
  userId,
  stageBadgeIds,
  completionPercentage,
  courseId,
  token
) => {
  try {
    const apiOptions = initApi(token)

    const body = JSON.stringify({
      userId,
      stageBadgeIds,
      completionPercentage,
      courseId,
    })

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-badge-completion`,
      {
        ...apiOptions,
        method: 'POST',
        body,
      }
    )

    const data = await response.json()

    if (!response.ok) {
      console.error(data.error)
      return
    }

    return data
  } catch (e) {
    console.error(e)
  }
}

export const getMyStages = async (userId, page, perPage) => {
  if (!userId) return []
  let query = supabase
    .from('stages')
    .select(
      `
            *,
            courses(
              *,
              course_badges(
                id,
                badges (*),
                user_badge_progress (
                    *
                )
              )
            ),
            stage_badges(
              id,
              badges (*),
              user_badge_progress (
                  *
              )
            )
        `,
      { count: 'exact' }
    )
    .eq('stage_badges.user_badge_progress.user_id', userId)
    .eq('courses.course_badges.user_badge_progress.user_id', userId)
    .range((page - 1) * perPage, page * perPage - 1)

  const { data, count, error } = await query

  if (error) {
    console.error('Error fetching course:', error.message)
    return []
  }

  return { stages: data, count }
}

export const assignBadge = async (userId, badgeIds, token) => {
  try {
    const apiOptions = initApi(token)

    const body = JSON.stringify({
      userId,
      badgeIds,
    })

    const response = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/assign-badge-v2`,
      {
        ...apiOptions,
        method: 'POST',
        body,
      }
    )

    const data = await response.json()

    if (!response.ok) {
      return { error: data.error }
    }

    return data
  } catch (e) {
    return { error: e.message }
  }
}

export const updateUser = async (userId, formData) => {
  const { data, error } = await supabase
    .from('users')
    .update([formData])
    .eq('id', userId)

  if (error) {
    console.error('Error update user data:', error.message)
    return []
  }

  return data
}

export const checkEmailExists = async (userId, email) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('id')
      .neq('id', userId)
      .eq('real_email', email)
    if (error) {
      console.error('Error update user data:', error.message)
    }
    return data.length > 0
  } catch (err) {
    console.error('Error checking email existence:', err)
    return false
  }
}
